import { MagpieConnection } from 'magpie-lib/connection';
import type { SubscriptionFilter } from 'magpie-lib/models';
import { marketplaceModalComponentDef } from 'navigation-components/config';
import { useNavigationComponent } from 'nest/application/hooks';
import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import * as t from 'ts-schema';
import {
  ButtonClicked,
  CloseModal,
  InstallComplete,
  InstallError,
  MarketplaceModalIFrameTopic,
  publishCloseModal,
} from '../components/internal/MarketplaceModalIFrame';
import { MarketplaceCardButtons } from '../types/marketplaceButton';
import { unsubscribeAll } from '../utils/unsubscribeAll';

export const marketplaceModalTypeSchema = t.union(
  t.literal('LISTING'),
  t.literal('EXPLORE'),
  t.literal('SUGGESTION'),
  t.literal('COLLECTION'),
  t.literal('INSTALL')
);

export type UseMarketplaceModalProps = {
  onClose?: () => void;
  onInstallComplete?: () => void;
  onInstallError?: () => void;

  // passthru props
  buttons?: MarketplaceCardButtons;
  modalType: t.SchemaType<typeof marketplaceModalTypeSchema>;
  title?: string;
  centered?: boolean;
};

export function useMarketplaceModal({
  onClose,
  onInstallComplete,
  onInstallError,
  buttons,
  ...passthruProps
}: UseMarketplaceModalProps) {
  const lastClosedTime = useRef(new Date().getTime());
  const modalInstanceId = useId();

  const navComponent = useNavigationComponent({
    componentDef: marketplaceModalComponentDef,
  });

  const buttonsWithKeys = useMemo(() => {
    return {
      primary: buttons?.primary?.map((b, i) => ({
        ...b,
        key: `primary-${i}`,
      })),
      secondary: buttons?.secondary?.map((b, i) => ({
        ...b,
        key: `secondary-${i}`,
      })),
    };
  }, [buttons]);

  // begin preloading after a 1 sec delay (to allow the connection to establish)
  useEffect(() => {
    setTimeout(async () => {
      const componentState = await navComponent.getState();

      // don't call hide() if the user was fast and already manually opened it
      if (componentState.isOpen) return;

      // hide will actually load the component
      await navComponent.hide();
    }, 1000);
  }, [navComponent]);

  // subscribe to modal events
  useEffect(() => {
    const topic = MagpieConnection.get().forTopic(MarketplaceModalIFrameTopic);

    return unsubscribeAll(
      topic.on(
        CloseModal.getType(),
        () => {
          lastClosedTime.current = new Date().getTime();
          onClose?.();
        },
        {
          filter: (({ payload }) =>
            payload.modalInstanceId === undefined ||
            payload.modalInstanceId === modalInstanceId) as SubscriptionFilter<
            typeof CloseModal
          >,
        }
      ),

      topic.on(
        ButtonClicked.getType(),
        ({ payload }) => {
          const combinedButtons = [
            ...(buttonsWithKeys?.primary ?? []),
            ...(buttonsWithKeys?.secondary ?? []),
          ];

          const matchingButton = combinedButtons?.find(
            (c) => c.key === payload.buttonKey
          );

          matchingButton?.onClick?.({
            close: () => publishCloseModal(modalInstanceId),
            listingId: payload.listingId,
            slug: payload.slug,
            sourceId: payload.sourceId,
          });
        },
        {
          filter: (({ payload }) =>
            payload.modalInstanceId === undefined ||
            payload.modalInstanceId === modalInstanceId) as SubscriptionFilter<
            typeof ButtonClicked
          >,
        }
      ),

      topic.on(InstallComplete.getType(), () => onInstallComplete?.(), {
        filter: (({ payload }) =>
          payload.modalInstanceId === undefined ||
          payload.modalInstanceId === modalInstanceId) as SubscriptionFilter<
          typeof InstallComplete
        >,
      }),
      topic.on(InstallError.getType(), () => onInstallError?.(), {
        filter: (({ payload }) =>
          payload.modalInstanceId === undefined ||
          payload.modalInstanceId === modalInstanceId) as SubscriptionFilter<
          typeof InstallError
        >,
      })
    );
  }, [
    buttonsWithKeys,
    modalInstanceId,
    onClose,
    onInstallComplete,
    onInstallError,
  ]);

  const open = useCallback(
    (dynamicPassthruProps?: Record<string, unknown>) => {
      // attempting to open the modal while it's still closing will make it become unresponsive,
      // so this is a bit of a hack to prevent speedy users from breaking things.
      if (new Date().getTime() - lastClosedTime.current < 500) return;

      const finalPassthruProps = {
        ...passthruProps,
        ...dynamicPassthruProps,
        modalInstanceId,

        // strip out the `onClick` properties so they don't make a console error when put in passthruProps
        buttons: {
          primary: buttonsWithKeys?.primary?.map((b) => {
            const { onClick: __onClick, ...rest } = b;
            return rest;
          }),
          secondary: buttonsWithKeys?.secondary?.map((b) => {
            const { onClick: __onClick, ...rest } = b;
            return rest;
          }),
        },
      };

      navComponent
        .setProps({
          passthruProps: {
            ...finalPassthruProps,
            // TODO: maybe delete all this `hideBackButton` and instead
            //       whether to show it based on the history stack
            hideBackButton:
              dynamicPassthruProps?.hideBackButton ??
              finalPassthruProps.modalType === 'LISTING',
          },
        })
        .then(() => navComponent.show().catch(console.error))
        .catch(console.error);
    },
    [buttonsWithKeys, modalInstanceId, navComponent, passthruProps]
  );

  return { open, close: () => publishCloseModal(modalInstanceId) };
}
